import React, { useState, useEffect } from 'react';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import {
  selectSelectedDatabases,
  selectSelectedSchemas,
  selectSelectedSchemaLevelObjects,
  selectSchemaLevelObject,
  unselectSchemaLevelObject,
} from '../reducers/builderSlice';
import { classNames } from 'utils/styleUtils';
import { ISchemaLevelObjectType } from 'utils/types';
import SnowflakeIcon from 'components/SnowflakeIcon';

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: '#4b5563',
  color: 'white',
  fontSize: 12,
  zIndex: 30,
  maxWidth: 500,
};

interface TooltipData {
  description: string;
}

interface ObjectItemProps {
  databaseName: string;
  schemaName: string;
  objectName: string;
  objectType: ISchemaLevelObjectType;
  mode: 'selection' | 'preview';
  highlighted?: boolean;
}

export default function ObjectItem(props: ObjectItemProps): JSX.Element {
  const dispatch = useAppDispatch();
  const selectedDatabases = useAppSelector(selectSelectedDatabases);
  const selectedSchemas = useAppSelector(selectSelectedSchemas);
  const selectedSchemaLevelObjects = useAppSelector(selectSelectedSchemaLevelObjects);
  const [selected, setSelected] = useState(false);

  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<TooltipData>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  useEffect(() => {
    const databaseSelected = selectedDatabases.find((database) => database.database === props.databaseName);
    const schemaSelected = selectedSchemas.find(
      (schema) => schema.schema === props.schemaName && schema.database === props.databaseName,
    );
    const schemaLevelObjectSelected = selectedSchemaLevelObjects.find(
      (object) =>
        object.database === props.databaseName &&
        object.schema === props.schemaName &&
        object.name === props.objectName &&
        object.type === props.objectType,
    );
    if (databaseSelected !== undefined || schemaSelected !== undefined || schemaLevelObjectSelected !== undefined) {
      setSelected(true);
    } else {
      setSelected(false);
    }
  }, [selectedDatabases, selectedSchemas, selectedSchemaLevelObjects]);

  const onClick = (): void => {
    if (selected) {
      dispatch(
        unselectSchemaLevelObject({
          type: props.objectType,
          name: props.objectName,
          schema: props.schemaName,
          database: props.databaseName,
        }),
      );
    } else {
      dispatch(
        selectSchemaLevelObject({
          type: props.objectType,
          name: props.objectName,
          schema: props.schemaName,
          database: props.databaseName,
        }),
      );
    }
    setSelected(!selected);
  };

  // Get the display name for the object type
  const getObjectTypeDisplayName = (): string => {
    switch (props.objectType) {
      case 'table':
        return 'Table';
      case 'view':
        return 'View';
      case 'function':
        return 'Function';
      case 'procedure':
        return 'Procedure';
      case 'file_format':
        return 'File Format';
      case 'sequence':
        return 'Sequence';
      case 'pipe':
        return 'Pipe';
      default:
        return 'Object';
    }
  };

  const objectTypeDisplayName = getObjectTypeDisplayName();

  return (
    <>
      <div
        className={classNames(
          'w-full relative items-center justify-start flex py-1 rounded-md border border-gray-300 bg-gray-50 text-gray-700 cursor-default',
          props.mode === 'selection' ? 'px-4' : 'px-2',
        )}
      >
        {props.mode === 'selection' && (
          <div className="absolute left-2">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onClick()}
              className="rounded border-gray-300 cursor-pointer"
            />
          </div>
        )}
        <div
          className={classNames('min-w-[20px] w-5 mr-1', props.mode === 'selection' ? 'ml-2' : '')}
          ref={containerRef}
        >
          <SnowflakeIcon objectType={props.objectType} highlighted={props.highlighted} />
        </div>
        <div
          className="text-sm truncate"
          onMouseLeave={() => {
            hideTooltip();
          }}
          onMouseMove={(event) => {
            const eventSvgCoords = localPoint(event);
            showTooltip({
              tooltipData: { description: `${props.objectName} (${objectTypeDisplayName})` },
              tooltipTop: eventSvgCoords?.y !== undefined ? eventSvgCoords.y + 10 : undefined,
              tooltipLeft: eventSvgCoords?.x,
            });
          }}
        >
          {props.objectName}
        </div>
      </div>
      {tooltipOpen && tooltipData !== undefined && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="text-sm">{tooltipData.description}</div>
        </TooltipInPortal>
      )}
    </>
  );
}
